import type { ReportingScopeOverrides } from '../services/dashboardData';

export type NurseNotesReportingScopeVariant = 'dashboard' | 'reportingTable';

export interface NurseNotesReportingScopeInput {
  /** `dashboard` matches `Dashboard.tsx`; `reportingTable` matches `ReportingTable.tsx` nurse_notes branch. */
  variant: NurseNotesReportingScopeVariant;
  mode: 'default' | 'team';
  categoryId?: string;
  reportingScope?: ReportingScopeOverrides | null;
  /** Sticky multi-select (รายงานรวม) or supervisor checkboxes — same IDs as Firestore paths. */
  selectedDepartmentIds: readonly string[];
  /**
   * When `variant` is `reportingTable` and this is true, a non-empty department
   * selection narrows the fetch. Used for the sticky กรองหน่วยงาน filter.
   */
  useSharedFiltersStickySubset?: boolean;
  isSupervisorNurse: boolean;
  hasWildcardScope: boolean;
  teamCategoryHospitalWide: boolean;
  /** Dashboard: `userAssignedDepartmentIdsForFirestore` after parent scope checks fail. */
  userAssignedDepartmentIdsForFirestore: readonly string[];
  /** ReportingTable: single selected department for narrow roles. */
  selectedDepartmentId?: string;
  /** ReportingTable: first scoped department fallback when `!canUseAllDepartmentDefault`. */
  scopedDepartmentFirstId?: string;
  canUseAllDepartmentDefault?: boolean;
}

const uniqueNonEmpty = (ids: readonly string[]): string[] =>
  Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)));

const withDepartmentIds = (
  reportingScope: ReportingScopeOverrides | null | undefined,
  departmentIds: string[],
): ReportingScopeOverrides => ({
  ...(reportingScope ?? {}),
  departmentIds,
});

const hasTeamWildcard = (
  mode: NurseNotesReportingScopeInput['mode'],
  reportingScope: ReportingScopeOverrides | null | undefined,
): boolean =>
  mode === 'team' &&
  Array.isArray(reportingScope?.departmentIds) &&
  reportingScope.departmentIds.includes('*');

const hasDepartmentIdsKey = (reportingScope: ReportingScopeOverrides | null | undefined): boolean =>
  reportingScope != null && typeof reportingScope === 'object' && 'departmentIds' in reportingScope;

/**
 * Single source of truth for nurse_notes fetch scope overrides.
 * Mirrors precedence in `Dashboard.tsx` / `ReportingTable.tsx` before this refactor.
 */
export function resolveNurseNotesReportingScope(
  input: NurseNotesReportingScopeInput,
): ReportingScopeOverrides | null | undefined {
  const selection = uniqueNonEmpty(input.selectedDepartmentIds);

  if (hasTeamWildcard(input.mode, input.reportingScope)) {
    return input.reportingScope;
  }

  const stickyApplies =
    input.mode === 'default' &&
    input.categoryId === 'nurse_notes' &&
    selection.length > 0 &&
    (input.variant === 'dashboard' ||
      (input.variant === 'reportingTable' && input.useSharedFiltersStickySubset === true));

  if (stickyApplies) {
    return withDepartmentIds(input.reportingScope, selection);
  }

  if (input.teamCategoryHospitalWide) {
    return withDepartmentIds(input.reportingScope, ['*']);
  }

  if (input.variant === 'reportingTable') {
    if (input.mode !== 'default' || hasDepartmentIdsKey(input.reportingScope)) {
      return input.reportingScope;
    }
  }

  if (input.isSupervisorNurse) {
    return input.reportingScope;
  }

  if (input.hasWildcardScope) {
    return input.reportingScope;
  }

  const parentIds = uniqueNonEmpty(
    Array.isArray(input.reportingScope?.departmentIds) ? input.reportingScope!.departmentIds! : [],
  ).filter((id) => id !== '*');

  if (input.variant === 'dashboard' && parentIds.length > 0) {
    return withDepartmentIds(input.reportingScope, parentIds);
  }

  if (input.variant === 'reportingTable') {
    const narrowId = input.selectedDepartmentId?.trim();
    if (narrowId) {
      return withDepartmentIds(input.reportingScope, [narrowId]);
    }
    if (input.canUseAllDepartmentDefault === false) {
      const first = input.scopedDepartmentFirstId?.trim();
      if (first) {
        return withDepartmentIds(input.reportingScope, [first]);
      }
    }
    const assigned = uniqueNonEmpty(input.userAssignedDepartmentIdsForFirestore);
    if (assigned.length > 0) {
      return withDepartmentIds(input.reportingScope, assigned);
    }
    return input.reportingScope;
  }

  const base = uniqueNonEmpty(input.userAssignedDepartmentIdsForFirestore);
  if (input.mode === 'default' && base.length > 0) {
    return withDepartmentIds(input.reportingScope, base);
  }

  return input.reportingScope;
}
